import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  filter,
  first,
  firstValueFrom,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import {
  CadFile,
  CadFileProcessingUpdateSchema,
  cadUploadFileTypeError,
  isCadUploadFile,
} from '../../../../models/cad-file.model';
import {
  CadObject,
  CadObjectShapeType,
} from '../../../../models/cad-object.model';
import { CadFileService } from '../../../services/cad-file.service';
import { CadProcessingService } from '../../../services/cad-processing.service';
import { RealtimeSocketService } from '../../../services/realtime/realtime-socket.service';
import { UploadCadComponent } from '../../../shared/upload-cad/upload-cad.component';
import { UseExistingCadComponent } from '../../../shared/use-existing-cad/use-existing-cad.component';
import { MouldingProcessingService } from '../../services/moulding-processing.service';

type UploadTab = 'upload' | 'existing';
type CadReadiness = 'terminal' | 'objects';
type Activity =
  | { kind: 'idle' }
  | { kind: 'uploading' }
  | { kind: 'processing'; statusText: string }
  | { kind: 'error'; message: string };

type Selection = {
  cadFileId: number;
  objects: CadObject[];
  selectedObjectId: number | null;
  saving: boolean;
  error: string | null;
};

@Component({
  selector: 'app-cad-input-picker-dialog',
  standalone: true,
  imports: [
    ButtonModule,
    DialogModule,
    FormsModule,
    MessageModule,
    RadioButtonModule,
    TabsModule,
    TooltipModule,
    UploadCadComponent,
    UseExistingCadComponent,
  ],
  templateUrl: './cad-input-picker-dialog.component.html',
})
export class CadInputPickerDialogComponent implements OnChanges {
  readonly processing = inject(MouldingProcessingService);
  private readonly cadFileService = inject(CadFileService);
  private readonly cadProcessing = inject(CadProcessingService);
  private readonly realtimeSocket = inject(RealtimeSocketService);

  @Input({ required: true }) nodeId!: string;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() configured = new EventEmitter<void>();

  readonly uploadTab = signal<UploadTab>('upload');
  readonly activity = signal<Activity>({ kind: 'idle' });
  readonly selection = signal<Selection | null>(null);

  get isBusy(): boolean {
    const activity = this.activity();
    return activity.kind === 'uploading' || activity.kind === 'processing';
  }

  get processingStatusText(): string | null {
    const activity = this.activity();
    return activity.kind === 'processing' ? activity.statusText : null;
  }

  get activityError(): string | null {
    const activity = this.activity();
    return activity.kind === 'error' ? activity.message : null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.reset();
    }
  }

  onVisibleChange(visible: boolean): void {
    this.visible = visible;
    this.visibleChange.emit(visible);
    if (!visible) {
      this.reset();
    }
  }

  setUploadTab(tab: UploadTab): void {
    this.uploadTab.set(tab);
    this.activity.set({ kind: 'idle' });
  }

  async onUploadCadFilesSelected(files: File[]): Promise<void> {
    const file = files[0];
    if (!file || this.isBusy) {
      return;
    }
    if (!isCadUploadFile(file)) {
      this.showError(cadUploadFileTypeError([file.name]));
      return;
    }

    this.activity.set({ kind: 'uploading' });
    try {
      const [cadFile] = await firstValueFrom(
        this.cadFileService.postCadFiles([file], { forMoulding: true }),
      );
      if (!cadFile) {
        throw new Error('Upload succeeded but no CAD file was returned.');
      }
      await this.waitForSelection(cadFile.id, 'terminal');
    } catch (error) {
      this.showError(
        error instanceof Error ? error.message : 'Failed to upload CAD file.',
      );
    }
  }

  async onExistingCadFilesSelected(cadFiles: CadFile[]): Promise<void> {
    const cadFile = cadFiles[0];
    if (!cadFile || this.isBusy) {
      return;
    }

    try {
      if (cadFile.cad_objects.length > 0) {
        this.showSelection(cadFile);
      } else {
        await this.waitForSelection(cadFile.id, 'objects');
      }
    } catch (error) {
      this.showError(
        error instanceof Error ? error.message : 'Failed to load CAD file.',
      );
    }
  }

  selectCadObject(selectedObjectId: number): void {
    const selection = this.selection();
    if (selection) {
      this.selection.set({ ...selection, selectedObjectId, error: null });
    }
  }

  async confirmCadSelection(): Promise<void> {
    const selection = this.selection();
    if (!selection?.selectedObjectId) {
      this.setSelectionError('Select a CAD object first.');
      return;
    }
    if (this.processing.downstreamExecutionActive()) {
      return;
    }

    this.selection.set({ ...selection, saving: true, error: null });
    try {
      await this.processing.setCadInputNode(this.nodeId, {
        cad_file_id: selection.cadFileId,
        primary_cad_object_id: selection.selectedObjectId,
      });
      this.configured.emit();
      this.onVisibleChange(false);
    } catch (error) {
      this.setSelectionError(
        error instanceof Error ? error.message : 'Failed to save CAD input.',
      );
    } finally {
      const currentSelection = this.selection();
      if (currentSelection) {
        this.selection.set({ ...currentSelection, saving: false });
      }
    }
  }

  showSources(): void {
    this.selection.set(null);
    this.activity.set({ kind: 'idle' });
  }

  reset(): void {
    this.uploadTab.set('upload');
    this.activity.set({ kind: 'idle' });
    this.selection.set(null);
  }

  private async waitForSelection(
    cadFileId: number,
    readiness: CadReadiness,
  ): Promise<void> {
    this.activity.set({
      kind: 'processing',
      statusText: 'Waiting for CAD processing',
    });
    this.showSelection(await this.waitForCadFile(cadFileId, readiness));
  }

  private waitForCadFile(
    cadFileId: number,
    readiness: CadReadiness,
  ): Promise<CadFile> {
    return firstValueFrom(
      this.realtimeSocket
        .listen({
          url: `/ws/cad-file/${cadFileId}`,
          eventType: 'cad.file_update',
          payloadKey: 'cad_file',
          schema: CadFileProcessingUpdateSchema,
        })
        .pipe(
          tap((update) =>
            this.activity.set({
              kind: 'processing',
              statusText: this.cadProcessing.statusText(update),
            }),
          ),
          filter((update) =>
            readiness === 'terminal'
              ? this.cadProcessing.isTerminal(update)
              : this.cadProcessing.hasObjects(update) ||
                this.cadProcessing.isTerminal(update),
          ),
          first(),
          switchMap((update) => {
            if (this.cadProcessing.isFailure(update)) {
              return throwError(
                () =>
                  new Error(
                    update.processing_error ||
                      'CAD file processing completed with errors.',
                  ),
              );
            }
            return this.cadFileService.getCadFile(cadFileId);
          }),
        ),
    );
  }

  private showSelection(cadFile: CadFile): void {
    const objects = this.cadFileService
      .flattenCadObjects(cadFile.cad_objects)
      .filter((cadObject) => cadObject.type === CadObjectShapeType.SOLID);
    if (objects.length === 0) {
      this.showError('No solid CAD objects found in this CAD file.');
      return;
    }

    this.activity.set({ kind: 'idle' });
    this.selection.set({
      cadFileId: cadFile.id,
      objects,
      selectedObjectId: objects[0]?.id ?? null,
      saving: false,
      error: null,
    });
  }

  private showError(message: string): void {
    this.activity.set({ kind: 'error', message });
  }

  private setSelectionError(error: string): void {
    const selection = this.selection();
    if (selection) {
      this.selection.set({ ...selection, error });
    }
  }
}
